import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, 
  Truck,
  Calendar, 
  Gauge,
  Download,
  Wrench,
  User,
  FileText,
  AlertCircle,
  CheckCircle,
  MapPin,
  Edit,
  Save,
  X,
  Upload,
  Eye,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Image as ImageIcon
} from 'lucide-react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { maintenanceService } from '../services/maintenanceService';
import { exitsService } from '../services/exitsService';
import { maintenancePhotosService } from '../services/maintenancePhotosService';
import requisitionValesService from '../services/requisitionValesService';

// Obtener la URL base del backend desde variables de entorno
const API_BASE_URL = process.env.REACT_APP_API_URL 
  ? process.env.REACT_APP_API_URL.replace('/api', '') 
  : 'http://localhost:5000';

const MaintenanceDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [maintenance, setMaintenance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploadingPDF, setUploadingPDF] = useState(false);
  const [deletingPDF, setDeletingPDF] = useState(false);
  const fileInputRef = useRef(null);
  
  // Estados para fotos de evidencia
  const [photos, setPhotos] = useState([]);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [generatingExcel, setGeneratingExcel] = useState(false);
  const [generatingVales, setGeneratingVales] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null); // Para el modal de vista previa
  const photoInputRef = useRef(null);

  useEffect(() => {
    const fetchMaintenanceDetail = async () => {
      try {
        setLoading(true);
        const data = await maintenanceService.getById(id);
        console.log('Detalle del mantenimiento:', data);
        setMaintenance(data);
        setLoading(false);
      } catch (err) {
        console.error('Error al cargar el detalle del mantenimiento:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchMaintenanceDetail();
    loadPhotos();
  }, [id]);

  // Función para cargar fotos
  const loadPhotos = async () => {
    try {
      const data = await maintenancePhotosService.getPhotos(id);
      setPhotos(data);
      console.log('📸 Fotos cargadas:', data);
    } catch (err) {
      console.error('Error cargando fotos:', err);
    }
  };

  // Funciones de navegación en el modal
  const handleNextPhoto = () => {
    const currentIndex = photos.findIndex(p => p.id === selectedPhoto.id);
    const nextIndex = (currentIndex + 1) % photos.length;
    setSelectedPhoto(photos[nextIndex]);
  };

  const handlePrevPhoto = () => {
    const currentIndex = photos.findIndex(p => p.id === selectedPhoto.id);
    const prevIndex = currentIndex === 0 ? photos.length - 1 : currentIndex - 1;
    setSelectedPhoto(photos[prevIndex]);
  };

  // Navegación con teclado
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!selectedPhoto) return;
      
      if (e.key === 'ArrowRight') handleNextPhoto();
      else if (e.key === 'ArrowLeft') handlePrevPhoto();
      else if (e.key === 'Escape') setSelectedPhoto(null);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedPhoto, photos]);

  // Función para subir fotos
  const handleUploadPhotos = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    try {
      setUploadingPhotos(true);
      const result = await maintenancePhotosService.uploadPhotos(
        id,
        files,
        'Usuario' // Aquí puedes pasar el usuario actual si lo tienes en contexto
      );
      
      alert(`✅ ${result.message}\nExpira: ${new Date(result.expires_at).toLocaleDateString('es-MX')}`);
      
      // Recargar fotos
      await loadPhotos();
      
      // Limpiar input
      if (photoInputRef.current) {
        photoInputRef.current.value = '';
      }
    } catch (err) {
      console.error('Error subiendo fotos:', err);
      alert('❌ Error al subir las fotos');
    } finally {
      setUploadingPhotos(false);
    }
  };

  // Función para eliminar una foto
  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta foto?')) return;

    try {
      await maintenancePhotosService.deletePhoto(photoId);
      alert('✅ Foto eliminada exitosamente');
      await loadPhotos();
    } catch (err) {
      console.error('Error eliminando foto:', err);
      alert('❌ Error al eliminar la foto');
    }
  };

  // Función para generar Excel con fotos
  const handleGenerateExcel = async () => {
    if (photos.length === 0) {
      alert('⚠️ No hay fotos para exportar');
      return;
    }

    try {
      setGeneratingExcel(true);
      await maintenancePhotosService.generateExcel(id);
      alert('✅ Excel generado y descargado exitosamente');
    } catch (err) {
      console.error('Error generando Excel:', err);
      alert('❌ Error al generar el archivo Excel');
    } finally {
      setGeneratingExcel(false);
    }
  };

  const handleGenerateValesExcel = async () => {
    // Verificar si hay al menos una requisición
    const hasRequisitions = [1, 2, 3, 4, 5, 6, 7].some(num => 
      maintenance[`Requisition_Number${num}`]
    );

    if (!hasRequisitions) {
      alert('⚠️ No hay requisiciones registradas en este mantenimiento');
      return;
    }

    try {
      setGeneratingVales(true);
      await requisitionValesService.generateValesExcel(id);
      alert('✅ Excel de vales generado y descargado exitosamente');
    } catch (err) {
      console.error('Error generando Excel de vales:', err);
      alert(`❌ ${err.message || 'Error al generar el archivo Excel de vales'}`);
    } finally {
      setGeneratingVales(false);
    }
  };

  const handleEdit = () => {
    setEditForm({
      KilometrajeActual: maintenance.KilometrajeActual || 0,
      TipoDeMantenimiento: maintenance.TipoDeMantenimiento || 'Preventivo',
      NombreOperador: maintenance.NombreOperador || '',
      NombreJefeTaller: maintenance.NombreJefeTaller || '',
      NombreMecanico: maintenance.NombreMecanico || '',
      NombreSupervisor: maintenance.NombreSupervisor || '',
      Requisition_Number1: maintenance.Requisition_Number1 || '',
      Requisition_Number2: maintenance.Requisition_Number2 || '',
      Requisition_Number3: maintenance.Requisition_Number3 || '',
      Requisition_Number4: maintenance.Requisition_Number4 || '',
      Requisition_Number5: maintenance.Requisition_Number5 || '',
      Requisition_Number6: maintenance.Requisition_Number6 || '',
      Requisition_Number7: maintenance.Requisition_Number7 || '',
      Observaciones: maintenance.Observaciones || ''
    });
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditForm({});
  };

  const handleSaveEdit = async () => {
    try {
      setSaving(true);
      await maintenanceService.update(id, editForm);
      
      // Recargar los datos
      const data = await maintenanceService.getById(id);
      setMaintenance(data);
      setIsEditing(false);
      setEditForm({});
      setSaving(false);
    } catch (err) {
      console.error('Error al actualizar mantenimiento:', err);
      setError(err.message);
      setSaving(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validar que sea PDF
    if (file.type !== 'application/pdf') {
      alert('Solo se permiten archivos PDF');
      return;
    }

    // Validar tamaño (10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('El archivo no debe superar los 10MB');
      return;
    }

    try {
      setUploadingPDF(true);
      await maintenanceService.uploadPDF(id, file);
      
      // Recargar datos para obtener el pdf_path actualizado
      const data = await maintenanceService.getById(id);
      setMaintenance(data);
      
      alert('PDF subido exitosamente');
    } catch (err) {
      console.error('Error al subir PDF:', err);
      alert('Error al subir el PDF: ' + err.message);
    } finally {
      setUploadingPDF(false);
      // Limpiar el input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleViewPDF = async () => {
    try {
      await maintenanceService.viewPDF(id);
    } catch (err) {
      console.error('Error al ver PDF:', err);
      alert('Error al abrir el PDF: ' + err.message);
    }
  };

  const handleRequisitionClick = async (requisitionNumber) => {
    if (!requisitionNumber || requisitionNumber.trim() === '') {
      return; // No hacer nada si está vacío
    }

    try {
      // Buscar la salida por número de requisición
      const exit = await exitsService.getByRequisitionNumber(requisitionNumber);
      
      if (exit && exit.id) {
        // Navegar a la página de detalle de la salida
        navigate(`/exits/exit/${exit.id}`);
      } else {
        // Mostrar alerta si no se encuentra
        alert(`❌ Número de requisición inválido o no existe: ${requisitionNumber}`);
      }
    } catch (err) {
      console.error('Error buscando requisición:', err);
      alert(`❌ Error al buscar la requisición: ${err.message}`);
    }
  };

  const handleDeletePDF = async () => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar este PDF?')) {
      return;
    }

    try {
      setDeletingPDF(true);
      await maintenanceService.deletePDF(id);
      
      // Recargar datos
      const data = await maintenanceService.getById(id);
      setMaintenance(data);
      
      alert('PDF eliminado exitosamente');
    } catch (err) {
      console.error('Error al eliminar PDF:', err);
      alert('Error al eliminar el PDF: ' + err.message);
    } finally {
      setDeletingPDF(false);
    }
  };

  const handleDownloadExcel = async () => {
    try {
      console.log(`📋 Generando Excel completo para mantenimiento ${id}`);
      await maintenanceService.downloadExcelComplete(id);
    } catch (err) {
      console.error('Error al generar Excel:', err);
      setError(err.message);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <motion.div
            className="w-16 h-16 border-4 border-orange-600 border-t-transparent rounded-full mx-auto mb-4"
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          />
          <p className="text-xl text-gray-600">Cargando detalles del mantenimiento...</p>
        </div>
      </div>
    );
  }

  if (error || !maintenance) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="bg-red-100 border-l-4 border-red-500 p-4 rounded shadow-md">
            <div className="flex items-center">
              <AlertCircle className="w-6 h-6 text-red-500 mr-3" />
              <div>
                <p className="font-bold text-red-700">Error</p>
                <p className="text-red-700">{error || 'No se pudo cargar el mantenimiento'}</p>
              </div>
            </div>
            <button
              onClick={() => navigate('/maintenances')}
              className="mt-4 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
            >
              Volver a Mantenimientos
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/maintenances')} className="text-orange-600 hover:text-orange-700">
              <ArrowLeft className="w-6 h-6" />
            </button>
            <h1 className="text-3xl font-bold text-gray-800">
              Detalle de Mantenimiento #{maintenance.IDMantenimiento}
            </h1>
          </div>
          <div className="flex gap-2">
            {isEditing ? (
              <>
                <button
                  onClick={handleCancelEdit}
                  className="flex items-center gap-2 bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors"
                  disabled={saving}
                >
                  <X className="w-5 h-5" />
                  Cancelar
                </button>
                <button
                  onClick={handleSaveEdit}
                  className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
                  disabled={saving}
                >
                  <Save className="w-5 h-5" />
                  {saving ? 'Guardando...' : 'Guardar'}
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={handleEdit}
                  className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Edit className="w-5 h-5" />
                  Editar
                </button>
                <button
                  onClick={handleDownloadExcel}
                  className="flex items-center gap-2 bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 transition-colors"
                >
                  <Download className="w-5 h-5" />
                  Descargar Excel
                </button>
              </>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Columna principal - Información del mantenimiento */}
          <div className="lg:col-span-2 space-y-6">
            {/* Información general */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-lg shadow-md p-6"
            >
              <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                <Wrench className="w-6 h-6 text-orange-600" />
                Información General
              </h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Tipo de Mantenimiento</p>
                  {isEditing ? (
                    <select
                      name="TipoDeMantenimiento"
                      value={editForm.TipoDeMantenimiento}
                      onChange={handleInputChange}
                      className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                    >
                      <option value="Preventivo">Preventivo</option>
                      <option value="Correctivo">Correctivo</option>
                    </select>
                  ) : (
                    <p className="font-semibold text-gray-800 flex items-center gap-2">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                        maintenance.TipoDeMantenimiento === 'Preventivo' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {maintenance.TipoDeMantenimiento || 'No especificado'}
                      </span>
                    </p>
                  )}
                </div>
                <div>
                  <p className="text-sm text-gray-500">Fecha de Mantenimiento</p>
                  <p className="font-semibold text-gray-800 flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    {(() => {
                      const date = new Date(maintenance.FechaDeMantenimiento);
                      const userTimezoneOffset = date.getTimezoneOffset() * 60000;
                      const localDate = new Date(date.getTime() + userTimezoneOffset);
                      return localDate.toLocaleDateString('es-MX', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      });
                    })()}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Kilometraje Actual</p>
                  {isEditing ? (
                    <input
                      type="number"
                      name="KilometrajeActual"
                      value={editForm.KilometrajeActual}
                      onChange={handleInputChange}
                      className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />
                  ) : (
                    <p className="font-semibold text-gray-800 flex items-center gap-2">
                      <Gauge className="w-4 h-4 text-gray-400" />
                      {maintenance.KilometrajeActual?.toLocaleString() || '0'} km
                    </p>
                  )}
                </div>
              </div>
            </motion.div>

            {/* Información del vehículo */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-lg shadow-md p-6"
            >
              <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                <Truck className="w-6 h-6 text-orange-600" />
                Información del Vehículo
              </h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Número Económico</p>
                  <p className="font-semibold text-gray-800">
                    {maintenance.numero_economico || maintenance.NumeroEconomico || 'No disponible'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Serie</p>
                  <p className="font-semibold text-gray-800">{maintenance.Serie || 'No disponible'}</p>
                </div>
                {maintenance.tipo_unidad && (
                  <div className="col-span-2">
                    <p className="text-sm text-gray-500">Tipo de Unidad</p>
                    <p className="font-semibold text-gray-800">{maintenance.tipo_unidad}</p>
                  </div>
                )}
              </div>
              {maintenance.truck_id && (
                <Link
                  to={`/trucks/${maintenance.truck_id}`}
                  className="mt-4 inline-flex items-center gap-2 text-orange-600 hover:text-orange-700"
                >
                  Ver detalles del vehículo →
                </Link>
              )}
            </motion.div>

            {/* Personal involucrado */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-lg shadow-md p-6"
            >
              <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                <User className="w-6 h-6 text-orange-600" />
                Personal Involucrado
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Operador</p>
                  {isEditing ? (
                    <input
                      type="text"
                      name="NombreOperador"
                      value={editForm.NombreOperador}
                      onChange={handleInputChange}
                      className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />
                  ) : (
                    <p className="font-semibold text-gray-800">{maintenance.NombreOperador || 'No especificado'}</p>
                  )}
                </div>
                <div>
                  <p className="text-sm text-gray-500">Jefe de Taller</p>
                  {isEditing ? (
                    <input
                      type="text"
                      name="NombreJefeTaller"
                      value={editForm.NombreJefeTaller}
                      onChange={handleInputChange}
                      className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />
                  ) : (
                    <p className="font-semibold text-gray-800">{maintenance.NombreJefeTaller || 'No especificado'}</p>
                  )}
                </div>
                <div>
                  <p className="text-sm text-gray-500">Mecánico</p>
                  {isEditing ? (
                    <input
                      type="text"
                      name="NombreMecanico"
                      value={editForm.NombreMecanico}
                      onChange={handleInputChange}
                      className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />
                  ) : (
                    <p className="font-semibold text-gray-800">{maintenance.NombreMecanico || 'No especificado'}</p>
                  )}
                </div>
                <div>
                  <p className="text-sm text-gray-500">Supervisor</p>
                  {isEditing ? (
                    <input
                      type="text"
                      name="NombreSupervisor"
                      value={editForm.NombreSupervisor}
                      onChange={handleInputChange}
                      className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />
                  ) : (
                    <p className="font-semibold text-gray-800">{maintenance.NombreSupervisor || 'No especificado'}</p>
                  )}
                </div>
              </div>
            </motion.div>

            {/* Observaciones */}
            {(maintenance.Observaciones || isEditing) && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-white rounded-lg shadow-md p-6"
              >
                <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <FileText className="w-6 h-6 text-orange-600" />
                  Observaciones
                </h2>
                {isEditing ? (
                  <textarea
                    name="Observaciones"
                    value={editForm.Observaciones}
                    onChange={handleInputChange}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                ) : (
                  <p className="text-gray-700 whitespace-pre-wrap">{maintenance.Observaciones}</p>
                )}
              </motion.div>
            )}

            {/* Documento PDF Adjunto */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="bg-white rounded-lg shadow-md p-6"
            >
              <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                <FileText className="w-6 h-6 text-orange-600" />
                Documento Adjunto
              </h2>
              
              {maintenance.pdf_path ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="text-sm text-green-700 font-medium">PDF adjunto disponible</span>
                  </div>
                  
                  <div className="flex gap-2">
                    <button
                      onClick={handleViewPDF}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <Eye className="w-4 h-4" />
                      Ver PDF
                    </button>
                    
                    <button
                      onClick={handleDeletePDF}
                      disabled={deletingPDF}
                      className="flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                    >
                      {deletingPDF ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          Eliminando...
                        </>
                      ) : (
                        <>
                          <Trash2 className="w-4 h-4" />
                          Eliminar
                        </>
                      )}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                    <AlertCircle className="w-5 h-5 text-gray-400" />
                    <span className="text-sm text-gray-600">No hay PDF adjunto</span>
                  </div>
                  
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="application/pdf"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingPDF}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50"
                  >
                    {uploadingPDF ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Subiendo PDF...
                      </>
                    ) : (
                      <>
                        <Upload className="w-5 h-5" />
                        Subir PDF
                      </>
                    )}
                  </button>
                  
                  <p className="text-xs text-gray-500 text-center">
                    Máximo 10MB • Solo archivos PDF
                  </p>
                </div>
              )}
            </motion.div>
          </div>

          {/* Columna lateral - Requisiciones */}
          <div className="space-y-6">
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-lg shadow-md p-6"
            >
              <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                <FileText className="w-6 h-6 text-orange-600" />
                Requisiciones de Material
              </h2>
              
              {/* Botón para generar Excel de vales */}
              {!isEditing && [1, 2, 3, 4, 5, 6, 7].some(num => maintenance[`Requisition_Number${num}`]) && (
                <button
                  onClick={handleGenerateValesExcel}
                  disabled={generatingVales}
                  className="w-full mb-4 flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {generatingVales ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      <span>Generando Excel...</span>
                    </>
                  ) : (
                    <>
                      <Download className="w-5 h-5" />
                      <span>Descargar Vales de Requisiciones</span>
                    </>
                  )}
                </button>
              )}
              
              <div className="space-y-3">
                {[1, 2, 3, 4, 5, 6, 7].map((num) => {
                  const requisicion = isEditing ? editForm[`Requisition_Number${num}`] : maintenance[`Requisition_Number${num}`];
                  return isEditing || requisicion ? (
                    <div 
                      key={num} 
                      className={`flex items-center gap-3 p-3 bg-gray-50 rounded-lg ${
                        !isEditing && requisicion ? 'hover:bg-blue-50 cursor-pointer transition-colors' : ''
                      }`}
                      onClick={() => {
                        if (!isEditing && requisicion) {
                          handleRequisitionClick(requisicion);
                        }
                      }}
                    >
                      {!isEditing && <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />}
                      <div className="flex-1">
                        <p className="text-xs text-gray-500 mb-1">Requisición {num}</p>
                        {isEditing ? (
                          <input
                            type="text"
                            name={`Requisition_Number${num}`}
                            value={requisicion || ''}
                            onChange={handleInputChange}
                            placeholder={`Número de requisición ${num}`}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                          />
                        ) : (
                          <p className="font-semibold text-gray-800">{requisicion}</p>
                        )}
                      </div>
                    </div>
                  ) : null;
                })}
                {!isEditing && ![1, 2, 3, 4, 5, 6, 7].some(num => maintenance[`Requisition_Number${num}`]) && (
                  <p className="text-gray-500 text-center py-4">No hay requisiciones registradas</p>
                )}
              </div>
            </motion.div>

            {/* Información adicional */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg shadow-md p-6"
            >
              <h3 className="text-lg font-bold text-gray-800 mb-3">Acciones Rápidas</h3>
              <div className="space-y-2">
                <button
                  onClick={handleDownloadExcel}
                  className="w-full flex items-center justify-center gap-2 bg-white text-orange-600 px-4 py-2 rounded-lg hover:bg-orange-50 transition-colors border border-orange-200"
                >
                  <Download className="w-4 h-4" />
                  Descargar Formato Excel
                </button>
                {maintenance.truck_id && (
                  <Link
                    to={`/trucks/${maintenance.truck_id}`}
                    className="w-full flex items-center justify-center gap-2 bg-white text-blue-600 px-4 py-2 rounded-lg hover:bg-blue-50 transition-colors border border-blue-200"
                  >
                    <Truck className="w-4 h-4" />
                    Ver Vehículo
                  </Link>
                )}
                <button
                  onClick={() => navigate('/maintenances')}
                  className="w-full flex items-center justify-center gap-2 bg-white text-gray-600 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors border border-gray-200"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Volver a Lista
                </button>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Sección de Fotos de Evidencia */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-8"
        >
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
                  <Upload className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800">📸 Fotos de Evidencia</h3>
                  <p className="text-sm text-gray-500">
                    {photos.length} foto{photos.length !== 1 ? 's' : ''} 
                    {photos.length > 0 && photos[0]?.expires_at && (
                      <span className="ml-2">
                        • Expiran: {new Date(photos[0].expires_at).toLocaleDateString('es-MX')}
                      </span>
                    )}
                  </p>
                </div>
              </div>
              
              <div className="flex gap-2">
                <input
                  ref={photoInputRef}
                  type="file"
                  multiple
                  accept="image/jpeg,image/jpg,image/png"
                  onChange={handleUploadPhotos}
                  className="hidden"
                />
                
                <button
                  onClick={() => photoInputRef.current?.click()}
                  disabled={uploadingPhotos}
                  className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
                >
                  {uploadingPhotos ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Subiendo...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      Subir Fotos
                    </>
                  )}
                </button>
                
                {photos.length > 0 && (
                  <button
                    onClick={handleGenerateExcel}
                    disabled={generatingExcel}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                  >
                    {generatingExcel ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Generando...
                      </>
                    ) : (
                      <>
                        <Download className="w-4 h-4" />
                        Generar Excel
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>

            {/* Información sobre límites */}
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                ℹ️ <strong>Información:</strong> Las fotos se almacenan por 7 días. 
                Máximo 10MB por foto • Formatos: JPG, PNG • Hasta 20 fotos por subida
              </p>
            </div>

            {/* Lista de fotos */}
            {photos.length > 0 ? (
              <div className="space-y-2">
                {photos.map((photo, index) => (
                  <motion.div
                    key={photo.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.03 }}
                    className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg hover:shadow-md transition-shadow group"
                  >
                    {/* Icono e información */}
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className="flex-shrink-0">
                        <ImageIcon className="w-8 h-8 text-purple-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-800 truncate" title={photo.photo_name}>
                          � {photo.photo_name}
                        </p>
                        <p className="text-sm text-gray-500">
                          {new Date(photo.uploaded_at).toLocaleDateString('es-MX', {
                            day: '2-digit',
                            month: 'long',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                    </div>

                    {/* Botones de acción */}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setSelectedPhoto(photo)}
                        className="p-2 bg-purple-100 text-purple-600 rounded-lg hover:bg-purple-200 transition-colors"
                        title="Ver imagen"
                      >
                        <Eye className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDeletePhoto(photo.id)}
                        className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors"
                        title="Eliminar"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
                <Upload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-500 mb-2">No hay fotos de evidencia</p>
                <p className="text-sm text-gray-400 mb-4">Sube fotos para documentar este mantenimiento</p>
                <button
                  onClick={() => photoInputRef.current?.click()}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  Subir Primera Foto
                </button>
              </div>
            )}
          </div>
        </motion.div>
      </div>

      {/* Modal de vista previa de imagen */}
      {selectedPhoto && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedPhoto(null)}
        >
          <div className="relative max-w-7xl max-h-[90vh] w-full h-full flex items-center justify-center">
            {/* Botón de cerrar */}
            <button
              onClick={() => setSelectedPhoto(null)}
              className="absolute top-4 right-4 p-2 bg-white rounded-full hover:bg-gray-100 transition-colors z-10 shadow-lg"
              title="Cerrar (Esc)"
            >
              <X className="w-6 h-6 text-gray-700" />
            </button>

            {/* Botones de navegación */}
            {photos.length > 1 && (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); handlePrevPhoto(); }}
                  className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-white rounded-full hover:bg-gray-100 transition-colors z-10 shadow-lg"
                  title="Anterior (←)"
                >
                  <ChevronLeft className="w-6 h-6 text-gray-700" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); handleNextPhoto(); }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-white rounded-full hover:bg-gray-100 transition-colors z-10 shadow-lg"
                  title="Siguiente (→)"
                >
                  <ChevronRight className="w-6 h-6 text-gray-700" />
                </button>
              </>
            )}

            {/* Imagen en tamaño completo */}
            <img
              src={`${API_BASE_URL}/${selectedPhoto.photo_path.replace(/\\/g, '/')}`}
              alt={selectedPhoto.photo_name}
              className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />

            {/* Información de la foto */}
            <div className="absolute bottom-4 left-4 right-4 bg-white bg-opacity-95 rounded-lg p-4 shadow-xl">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="font-semibold text-gray-800">📷 {selectedPhoto.photo_name}</p>
                  <p className="text-sm text-gray-600">
                    Subida el {new Date(selectedPhoto.uploaded_at).toLocaleDateString('es-MX', {
                      day: '2-digit',
                      month: 'long',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
                {photos.length > 1 && (
                  <div className="ml-4 text-sm font-medium text-gray-600 bg-gray-100 px-3 py-1 rounded-full">
                    {photos.findIndex(p => p.id === selectedPhoto.id) + 1} / {photos.length}
                  </div>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default MaintenanceDetailPage;
